import React from 'react';
import { Check, X, GraduationCap, BookOpen, Presentation, Briefcase, Lightbulb, Users, FileText, Award, Target, MonitorPlay, Settings, Star } from 'lucide-react';

const Consulting: React.FC = () => {
  const serviceAreas = [
    {
      icon: <GraduationCap size={32} />,
      title: "Capacitación Docente",
      description: "Talleres prácticos para profesores sobre uso pedagógico de Moodle, creación de actividades interactivas y evaluación en línea.",
      benefit: "Docentes empoderados que sacan el máximo provecho a las herramientas digitales."
    },
    {
      icon: <Settings size={32} />, // Reusing Settings icon but importing it below as it was missing in imports list used in previous steps, wait, let me check imports. I'll use Briefcase or similar if Settings is not imported. I see Briefcase. I'll use that or add Settings. Let's add Settings to import.
      title: "Formación de Administradores",
      description: "Curso técnico avanzado para encargados de plataforma: gestión de usuarios, cursos, permisos, plugins y mantenimiento básico.",
      benefit: "Equipo interno capaz de resolver el 90% de los requerimientos cotidianos con autonomía."
    },
    {
      icon: <BookOpen size={32} />,
      title: "Diseño Instruccional",
      description: "Consultoría para estructurar contenidos educativos, guionización de cursos y creación de recursos multimedia de alto impacto.",
      benefit: "Cursos atractivos, pedagógicamente efectivos y con altas tasas de finalización."
    },
    {
      icon: <Lightbulb size={32} />,
      title: "Consultoría Estratégica",
      description: "Asesoría a directivos para la transformación digital de la institución, selección de tecnologías y planificación de crecimiento.",
      benefit: "Hoja de ruta clara para la inversión en tecnología educativa."
    },
    {
      icon: <FileText size={32} />,
      title: "Auditoría Pedagógica",
      description: "Revisión y diagnóstico de cursos existentes para proponer mejoras en estructura, usabilidad y evaluación.",
      benefit: "Mejora continua de la calidad educativa ofrecida."
    },
    {
      icon: <MonitorPlay size={32} />,
      title: "Producción de Contenidos",
      description: "Servicio de factoría de contenidos SCORM, videos interactivos y material gráfico para tus cursos.",
      benefit: "Material profesional sin necesidad de contratar un equipo de diseño interno."
    }
  ];

  // Fix: Add Settings to import
  // Wait, I can't edit imports in `const serviceAreas`. I need to ensure imports are correct at top.
  // I will check imports in my `create_file` call above. Check, X, GraduationCap, BookOpen, Presentation, Briefcase, Lightbulb, Users, FileText, Award, Target, MonitorPlay are imported. Settings is missing. I will add it.

  const plans = [
    {
      name: "Taller Práctico",
      price: "$350.000",
      period: "/taller 4h",
      description: "Capacitación intensiva",
      popular: false,
      features: [
        { text: "Sesión sincrónica presencial/Zoom", included: true },
        { text: "Hasta 15 participantes", included: true },
        { text: "Material digital", included: true },
        { text: "Grabación de la sesión", included: true },
        { text: "Certificado de participación", included: true },
        { text: "Acceso a aula de práctica", included: true },
        { text: "Manuales paso a paso", included: false },
        { text: "Soporte post-taller", included: false }
      ],
      buttonText: "Cotizar Taller",
      buttonClass: "btn-secondary"
    },
    {
      name: "Certificación",
      price: "$120.000",
      period: "/persona",
      description: "Curso asincrónico tutorizado",
      popular: true,
      features: [
        { text: "20 horas de formación", included: true },
        { text: "Acceso 24/7 a plataforma", included: true },
        { text: "Tutor experto asignado", included: true },
        { text: "Foros y tareas prácticas", included: true },
        { text: "Certificado de aprobación", included: true },
        { text: "Manuales descargables", included: true },
        { text: "Feedback personalizado", included: true },
        { text: "Networking con pares", included: true }
      ],
      buttonText: "Inscribir Equipo",
      buttonClass: "btn-primary"
    },
    {
      name: "Consultoría",
      price: "$2.5 UF",
      period: "/hora",
      description: "Asesoría experta",
      popular: false,
      features: [
        { text: "Reuniones de trabajo", included: true },
        { text: "Revisión de plataformas", included: true },
        { text: "Informes de diagnóstico", included: true },
        { text: "Planificación estratégica", included: true },
        { text: "Selección de personal", included: true },
        { text: "Definición de procesos", included: true },
        { text: "Mentoria a directivos", included: true },
        { text: "Pack mínimo 10 horas", included: true }
      ],
      buttonText: "Agendar Hora",
      buttonClass: "btn-secondary"
    }
  ];

  const benefits = [
    {
      icon: <Target size={24} />,
      title: "A la Medida",
      description: "Contenidos adaptados a la realidad y necesidades de tu institución"
    },
    {
      icon: <Users size={24} />,
      title: "Autonomía",
      description: "Dejamos la capacidad instalada en tu equipo, no creamos dependencia"
    },
    {
      icon: <Award size={24} />,
      title: "Certificable",
      description: "Entregamos diplomas que validan las competencias adquiridas"
    },
    {
      icon: <Briefcase size={24} />,
      title: "Práctico",
      description: "Enfoque 100% manos a la obra, resolviendo problemas reales"
    }
  ];

  const useCases = [
    {
      title: "Adopción Digital",
      description: "Colegio implementa Moodle pero los profesores no lo usan. Implementamos un plan de capacitación y acompañamiento para lograr la adopción.",
      results: "100% docentes activos en 3 meses"
    },
    {
      title: "Renovación Curricular",
      description: "Instituto desea pasar sus carreras presenciales a modalidad híbrida. Asesoramos en el diseño instruccional y estructura de aulas virtuales.",
      results: "Lanzamiento exitoso de 5 carreras semipresenciales"
    },
    {
      title: "Capacitación TI",
      description: "Nuevo administrador de plataforma necesita dominar Moodle rápidamente. Curso intensivo personalizado para asumir el cargo.",
      results: "Administrador operativo en 1 semana"
    }
  ];

  return (
    <section id="consulting" className="consulting-landing">
      {/* Hero Section */}
      <div className="crm-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-left">
              <div className="hero-badge">
                <GraduationCap size={16} />
                Formación y Estrategia
              </div>
              <h1 className="hero-title">
                Capacitación y Consultoría
                <span className="highlight">eLearning</span>
              </h1>
              <p className="hero-description">
                Potencia el talento humano de tu institución. Te ayudamos a adoptar tecnologías, 
                diseñar mejores cursos y gestionar el cambio hacia la educación digital.
              </p>
              <div className="hero-cta">
                <a href="#contacto" className="btn btn-primary btn-lg">
                  Ver Disponibilidad
                </a>
              </div>
            </div>
            <div className="hero-right">
              <div className="hero-stats">
                <div className="stat-card">
                  <div className="stat-number">+2000</div>
                  <div className="stat-label">Profesores capacitados</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">50+</div>
                  <div className="stat-label">Instituciones asesoradas</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">4.9/5</div>
                  <div className="stat-label">Evaluación de cursos</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="container">
        <div className="service-areas">
          <div className="section-header">
            <h2>Transferencia de Conocimiento</h2>
            <p className="section-subtitle">
              No solo implementamos tecnología, te enseñamos a usarla estratégicamente
            </p>
          </div>
          
          <div className="service-grid">
            {serviceAreas.map((area, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {area.icon}
                </div>
                <h4>{area.title}</h4>
                <p className="service-description">{area.description}</p>
                <div className="service-benefit">
                  <strong>Beneficio:</strong> {area.benefit}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="benefits-section">
          <h3>Formación con sentido</h3>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="use-cases-section">
          <div className="section-header">
            <h2>Transformación Real</h2>
            <p className="section-subtitle">
              Impacto directo en la calidad educativa y eficiencia operativa
            </p>
          </div>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <h4>{useCase.title}</h4>
                <p className="use-case-description">{useCase.description}</p>
                <div className="use-case-results">
                  <Check size={20} className="check-icon" />
                  <span>{useCase.results}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Plans */}
        <div id="planes" className="pricing-section">
          <div className="section-header">
            <h2>Opciones de Formación</h2>
            <p className="section-subtitle">
              Programas flexibles para equipos de todos los tamaños
            </p>
          </div>
          
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && (
                  <div className="popular-badge">
                    <Star size={16} />
                    Recomendado
                  </div>
                )}
                
                <div className="pricing-header">
                  <h4>{plan.name}</h4>
                  <div className="price">
                    <span className="amount">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="features-list">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="feature-item">
                      {feature.included ? (
                        <Check size={20} className="feature-check" />
                      ) : (
                        <X size={20} className="feature-cross" />
                      )}
                      <span className={feature.included ? '' : 'feature-disabled'}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="pricing-footer">
                  <a href="#contacto" className={`btn ${plan.buttonClass} btn-block`}>
                    {plan.buttonText}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="final-cta">
          <div className="cta-content">
            <h3>¿Tu equipo necesita actualizarse?</h3>
            <p>
              Diseñamos un plan de capacitación a la medida de tus necesidades y brechas digitales.
            </p>
            <div className="cta-buttons">
              <a href="#contacto" className="btn btn-primary btn-lg">
                Solicitar Propuesta
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Consulting;
